import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { TOKEN_MAPBOX } from 'utils/constants'

import style from './SearchControl.module.css'

interface GeocodingFeature {
  place_name: string
  center: [number, number]
  place_type: string[]
}

const ZOOM_BY_TYPE: Record<string, number> = {
  country: 6,
  region: 8,
  place: 15,
  address: 17,
  poi: 18,
}

const SearchControl = ({ map, right, panelOffset = 0 }: { map?: any; right?: number; panelOffset?: number }) => {
  const [expanded, setExpanded] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GeocodingFeature[]>([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<number | undefined>(undefined)
  const rootRef = useRef<HTMLDivElement>(null)

  const collapse = () => {
    setExpanded(false)
    setQuery('')
    setResults([])
  }

  const handleToggle = () => {
    if (expanded) {
      collapse()
    } else {
      setExpanded(true)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }

  const search = async (q: string) => {
    if (!q.trim()) {
      setResults([])
      return
    }
    setLoading(true)
    try {
      const center = map?.getCenter()
      const proximity = center ? `&proximity=${center.lng},${center.lat}` : ''
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        q
      )}.json?access_token=${TOKEN_MAPBOX}&types=country,region,place,address,poi&limit=5&fuzzyMatch=true${proximity}`
      const res = await fetch(url)
      const data = await res.json()
      setResults(data.features ?? [])
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value
    setQuery(q)
    clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => search(q), 400)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') collapse()
  }

  const handleSelect = (feature: GeocodingFeature) => {
    const type = feature.place_type?.[0]
    const zoom = ZOOM_BY_TYPE[type] ?? 14
    const padding = panelOffset > 0 ? { left: panelOffset + 40, top: 0, right: 0, bottom: 0 } : undefined
    map?.flyTo({ center: feature.center, zoom, padding })
    collapse()
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        collapse()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div ref={rootRef} className={style.root} style={right !== undefined ? { right: `${right}px` } : undefined}>
      <div className={style.container}>
        <div className={clsx(style.inputWrapper, { [style.inputWrapperExpanded]: expanded })}>
          <input
            ref={inputRef}
            className={style.input}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Search country, city, address…"
          />
        </div>
        <button className={style.button} onClick={handleToggle} title="Search">
          {loading ? (
            <span className={style.loader} />
          ) : (
            <svg className={style.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
              <circle cx="10.5" cy="10.5" r="6.5" />
              <line x1="15.5" y1="15.5" x2="21" y2="21" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>
      {expanded && results.length > 0 && (
        <div className={style.results}>
          {results.map((feature, i) => (
            <div key={i} className={style.resultItem} onClick={() => handleSelect(feature)}>
              {feature.place_name}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SearchControl
